import { ANNOTATION_MODES, GRAYSCALE_MODES } from "../config/config";
import Arrow from "./annotations/arrow";
import Text from "./annotations/text";
import Redaction from "./annotations/redaction";
import OutlineBox from "./annotations/outlinebox";
import FreeHand from "./annotations/freeHand";
import Angle from "./annotations/angle";
import LengthLine from "./annotations/lengthLine";
import Area from "./annotations/area";
import CupToDisc from "./annotations/cupToDisc";
import FreeHandArea from "./annotations/freeHandArea";
import StandardCircles1 from "./annotations/standardCircles1";
import StandardCircles2 from "./annotations/standardCircles2";
import DrusenCircles1 from "./annotations/drusenCircles1";
import DrusenCircles2 from "./annotations/drusenCircles2";
import DrusenCircles3 from "./annotations/drusenCircles3";
import DrusenCircles4 from "./annotations/drusenCircles4";
import PDT from "./annotations/pdt";
import { bound } from "./geometry";
import ETDRSGrid from "./annotations/etdrsGrid";
import CanvasRenderer from "./canvasRenderer";
import ContinuousDraw from "./annotations/continuousDraw";
import Rectangle from "./annotations/rectangle"
import DeepDeeAnalysisChart from "./annotations/deepDeeAnalysisChart"

export function defaultColorSettings(grayscaleMode, isOptos) {
  return {
    grayscaleMode: grayscaleMode || GRAYSCALE_MODES.None,
    brightness: DEFAULT_BRIGHTNESS,
    contrast: DEFAULT_CONTRAST,
    gamma: DEFAULT_GAMMA,
    isOptos,
    greenBrightness: DEFAULT_BRIGHTNESS,
    greenContrast: DEFAULT_CONTRAST
  };
}

export function octColorSettings() {
  return {
    isInverted: false,
    colorPalette: null
  };
}

export function thicknessMapColorSettings(palette){
  return {
    colorPalette: palette,
    linearAdjustment: null
  };
}

// for the numbers,  see: https://en.wikipedia.org/wiki/Luma_(video)
export function grayscaleREC709(pixel) {
  return Math.floor(getByte(pixel, RED) * 0.2126
    + getByte(pixel, GREEN) * .7152
    + getByte(pixel, BLUE) * 0.0722);
}

export function grayscaleRed(pixel) {
  return getByte(pixel, RED);
}

export function grayscaleGreen(pixel) {
  return getByte(pixel, GREEN);
}

export function grayscaleBlue(pixel) {
  return getByte(pixel, BLUE);
}

export const GRAYSCALE_SETTINGS = {
  None: null,
  REC709: { key: "REC709", pixelRule: grayscaleREC709 },
  Red: { key: "Red", pixelRule: grayscaleRed },
  Green: { key: "Green", pixelRule: grayscaleGreen },
  Blue: { key: "Blue", pixelRule: grayscaleBlue }
}

const RED = 0;
const GREEN = 8;
const BLUE = 16;
const ALPHA = 24;

export function applyGrayscaleRule(pixel, rule) {
  let gray = rule(pixel);
  return createPixel(gray, gray, gray, getByte(pixel, ALPHA));
}

// see - https://www.html5rocks.com/en/tutorials/canvas/imagefilters/
// brightness in (-0.5, 0.5)
export function getBrightnessTable(brightness) {
  const brightnessTable = new Uint8Array(256);
  const adjustment = brightness * 255;
  for (let i = 0; i < 256; i++) brightnessTable[i] = bound(i + adjustment, 0, 255);
  return brightnessTable;
}

// see - https://stackoverflow.com/questions/10521978/html5-canvas-image-contrast
// contrast in (0.5, 1.5)
export function getContrastTable(contrast) {
  const contrastTable = new Uint8Array(256);
  const intercept = 128 * (1 - contrast);
  for (let i = 0; i < 256; i++) contrastTable[i] = bound(i * contrast + intercept, 0, 255);
  return contrastTable;
}

// see - https://stackoverflow.com/questions/14012221/gamma-adjustment-on-the-html5-canvas
// gamma in (0.1, 1.9)
export function getGammaTable(gamma) {
  const gammaTable = new Uint8Array(256);
  for (let i = 0; i < 256; i++) gammaTable[i] = bound(255 * Math.pow(i / 255, 1 / gamma), 0, 255);
  return gammaTable;
}

export const DEFAULT_BRIGHTNESS = 0;
export const DEFAULT_CONTRAST = 1;
export const DEFAULT_GAMMA = 1;

export function applyColorRule(pixel, colorTable) {
  const r = colorTable[getByte(pixel, RED)];
  const g = colorTable[getByte(pixel, GREEN)];
  const b = colorTable[getByte(pixel, BLUE)];
  return createPixel(r, g, b, getByte(pixel, ALPHA));
}

export function applyGreenColorRule(pixel, colorTable) {
  const r = getByte(pixel, RED);
  const g = colorTable[getByte(pixel, GREEN)];
  const b = getByte(pixel, BLUE);
  return createPixel(r, g, b, getByte(pixel, ALPHA));
}

export function getByte(value, n) {
  return (value >> n) & 0xff;
}

export function createPixel(r, g, b, a) {
  return createShiftedByte(r, RED)
    | createShiftedByte(g, GREEN)
    | createShiftedByte(b, BLUE)
    | createShiftedByte(a, ALPHA);
}

function createShiftedByte(value, n) {
  return (value & 0xff) << n;
}

export function hasCustomPixelColorSettings(settings) {
  return settings.grayscaleMode !== ""
    || settings.brightness !== DEFAULT_BRIGHTNESS
    || settings.contrast !== DEFAULT_CONTRAST
    || settings.gamma !== DEFAULT_GAMMA
    || (settings.isOptos && (
      settings.greenBrightness !== DEFAULT_BRIGHTNESS
      || settings.greenContrast !== DEFAULT_CONTRAST
    ));
}

export function hasCustomOCTColorSettings(settings) {
  return settings.isInverted || !!settings.colorPalette;
}

export function hasCustomThicknessColorSettings(settings){
  return !!settings.colorPalette || !!settings.linearAdjustment;
}

export function applyPixelShadingRules(readBuffer, writeBuffer, colorSettings) {
  let { brightness, contrast, gamma, greenBrightness, greenContrast, grayscaleMode } = colorSettings;
  let grayscaleSetting = GRAYSCALE_SETTINGS[grayscaleMode];
  let grayscaleRule = grayscaleSetting && grayscaleSetting.pixelRule;

  let brightnessTable = (brightness !== DEFAULT_BRIGHTNESS) && getBrightnessTable(brightness);
  let contrastTable = (contrast !== DEFAULT_CONTRAST) && getContrastTable(contrast);
  let gammaTable = (gamma !== DEFAULT_GAMMA) && getGammaTable(gamma);
  let greenBrightnessTable = (greenBrightness !== DEFAULT_BRIGHTNESS) && getBrightnessTable(greenBrightness);
  let greenContrastTable = (greenContrast !== DEFAULT_CONTRAST) && getContrastTable(greenContrast);
  for (let i = 0; i < readBuffer.length; i++) {
    let pixel = readBuffer[i];

    if (grayscaleRule) {
      let isGreen = grayscaleMode === GRAYSCALE_MODES.Green;
      let gray = grayscaleRule(pixel);

      if (gammaTable) gray = gammaTable[gray];
      if (contrastTable) gray = contrastTable[gray];
      if (isGreen && greenContrastTable) gray = greenContrastTable[gray];
      if (brightnessTable) gray = brightnessTable[gray];
      if (isGreen && greenBrightnessTable) gray = greenBrightnessTable[gray];

      pixel = createPixel(gray, gray, gray, getByte(pixel, ALPHA));
    } else {
      if (gammaTable) pixel = applyColorRule(pixel, gammaTable);
      if (contrastTable) pixel = applyColorRule(pixel, contrastTable);
      if (greenContrastTable) pixel = applyGreenColorRule(pixel, greenContrastTable);
      if (brightnessTable) pixel = applyColorRule(pixel, brightnessTable);
      if (greenBrightnessTable) pixel = applyGreenColorRule(pixel, greenBrightnessTable);
    }

    writeBuffer[i] = pixel;
  }
}

export function setMinimalSize(canvas, img, size){
  canvas.width = Math.min(img.width, size.width || size.w);
  canvas.height = Math.min(img.height, size.height || size.h);
}

export function drawShadedImage(img, canvas, rule, settings){
  if (!canvas.width || !canvas.height) return false;

  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const writeData = ctx.createImageData(canvas.width, canvas.height);
  let writeBuffer = new ArrayBuffer(imgData.data.length);
  rule(new Uint32Array(imgData.data.buffer), new Uint32Array(writeBuffer), settings);
  writeData.data.set(new Uint8ClampedArray(writeBuffer));
  ctx.putImageData(writeData, 0, 0);
  return true;
}

export function exportBlob(canvas, mimeType = "image/jpeg", quality = 1) {
  return new Promise(resolve => {
    if (canvas.toBlob) canvas.toBlob(resolve, mimeType, 1);
    else if (canvas.msToBlob) {
      let blob = canvas.msToBlob();
      resolve(blob);
    } else {
      const dataUrl = canvas.toDataUrl(mimeType, quality);
      let blob = generateBlobFromDataUrl(dataUrl, mimeType);
      resolve(blob);
    }
  })
}

export function generateBlobFromDataUrl(dataUrl, mimeType = "image/jpeg") {
  const base64 = dataUrl.split(",")[1];
  return generateBlobFromBase64(base64, mimeType);
}

export function generateBlobFromBase64(base64, mimeType = "image/jpeg") {
  const data = atob(base64);
  const arr = new Uint8Array(data.length);
  for (let i = 0; i < data.length; i++) arr[i] = data.charCodeAt(i);
  return new Blob([arr], { type: mimeType });
}

export function loadImageFromBlob(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      img.onload = null;
      img.onerror = null;
      resolve({img, url});
    }
    img.onerror = () => {
      img.onload = null;
      img.onerror = null;
      console.error("failed to load image blob", blob);
      URL.revokeObjectURL(url);
      reject();
    }
    img.src = url;
  });
}

export function loadImageFromBase64(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject
    img.src = src;
  });
}

export function getAnnotationType(mode) {
  switch (mode) {
    case ANNOTATION_MODES.Arrow: return Arrow;
    case ANNOTATION_MODES.Text: return Text;
    case ANNOTATION_MODES.Redact: return Redaction;
    case ANNOTATION_MODES.Outline: return OutlineBox;
    case ANNOTATION_MODES.FreeHandLine: return FreeHand;
    case ANNOTATION_MODES.ContinuousDraw: return ContinuousDraw;
    case ANNOTATION_MODES.Angle: return Angle;
    case ANNOTATION_MODES.LengthLine: return LengthLine;
    case ANNOTATION_MODES.Area: return Area;
    case ANNOTATION_MODES.CupToDisc: return CupToDisc;
    case ANNOTATION_MODES.FreeHandArea: return FreeHandArea;
    case ANNOTATION_MODES.PDT: return PDT;
    case ANNOTATION_MODES.ETDRSGrid: return ETDRSGrid;
    case ANNOTATION_MODES.StandardCircles1: return StandardCircles1;
    case ANNOTATION_MODES.StandardCircles2: return StandardCircles2;
    case ANNOTATION_MODES.DrusenCircles1: return DrusenCircles1;
    case ANNOTATION_MODES.DrusenCircles2: return DrusenCircles2;
    case ANNOTATION_MODES.DrusenCircles3: return DrusenCircles3;
    case ANNOTATION_MODES.DrusenCircles4: return DrusenCircles4;
    case ANNOTATION_MODES.Rectangle: return Rectangle;
    case ANNOTATION_MODES.DeepDeeInfo: return DeepDeeAnalysisChart;
    default: return null;
  }
}

export const boxSize = 17;
export const boxLineWidth = 1;
export const boxColor = "red";

export function setBoxStyle(ctx) {
  ctx.lineWidth = boxLineWidth;
  ctx.fillStyle = boxColor;
  ctx.strokeStyle = boxColor;
}

export function drawBoxes(ctx, points, zoomLevel = 1.0) {
  points.forEach(b => drawBox(ctx, b.x, b.y, zoomLevel));
}

export function drawBox(ctx, x, y, zoomLevel = 1.0, size = boxSize) {
  let newSize = Math.max(size/zoomLevel, size);
  setBoxStyle(ctx);
  ctx.beginPath();
  ctx.strokeRect(x - newSize, y - newSize, newSize * 2, newSize * 2);
  ctx.closePath();
}

export const lineColor = "white";
export const lineWidth = 3;

export function setLineStyle(ctx, annotation) {
  ctx.fillStyle = annotation.color || lineColor;
  ctx.strokeStyle = annotation.color || lineColor;
  ctx.lineWidth = annotation.lineWidth || lineWidth;
}

export const font = "25px Calibri";
export const fontColor = "white";
export const baseline = "hanging";
export const measuringCanvas = document.createElement("canvas");
export const measuringCtx = measuringCanvas.getContext("2d");

export function measureText(text, font = "25px Calibri") {
  measuringCtx.font = font;
  return measuringCtx.measureText(text).width;
}

export function setFontStyle(ctx, annotation) {
  ctx.font = annotation.font || font;
  ctx.fillStyle = annotation.fontColor || annotation.color || fontColor;
  ctx.textBaseline = annotation.baseline || baseline;
}

const hexRegex = /^#?(([a-f0-9]{2})([a-f0-9]{2})([a-f0-9]{2}))|(([a-f0-9]{1})([a-f0-9]{1})([a-f0-9]{1}))$/i;
export function hexToRgb(color) {
  if (color.length !== 4 && color.length !== 7) return null;

  let result = hexRegex.exec(color);
  if (result === null) return null;

  let r = 0, g = 0, b = 0;
  if (result[1]) {
    // matched a 6-digit color
    r = parseInt(result[2], 16);
    g = parseInt(result[3], 16);
    b = parseInt(result[4], 16);
  }
  else if (result[5]) {
    // matched a 3 digit color
    r = parseInt(result[6] + result[6], 16);
    g = parseInt(result[7] + result[7], 16);
    b = parseInt(result[8] + result[8], 16);
  }
  return { r, g, b };
}

export function getWebglContext(canvas) {
  return canvas.getContext("webgl2") || canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
}

export function isContextLost(context) {
  return context.isContextLost();
}

export const octThicknessGradient = [
  [0.000, 0x000000],
  [0.229, 0x072797],
  [0.263, 0x0039B4],
  [0.291, 0x004CBE],
  [0.330, 0x008BCC],
  [0.362, 0x00AFCB],
  [0.388, 0x00CDC1],
  [0.400, 0x00DDA7],
  [0.437, 0x00E75E],
  [0.472, 0x06F800],
  [0.476, 0x0CF900],
  [0.502, 0x30FF00],
  [0.584, 0xDEFE00],
  [0.620, 0xFFF800],
  [0.690, 0xFFA000],
  [0.722, 0xFF6800],
  [0.878, 0xFF1010],
  [0.882, 0xFF1616],
  [0.906, 0xFF4C4C],
  [0.910, 0xFF5555],
  [0.929, 0xFF9696],
  [0.941, 0xFFBCBC],
  [0.957, 0xFFDDDD],
  [0.969, 0xFFF1F1],
  [1.000, 0xFFFFFF]
];

export const heidelbergThicknessGradient = [
  [0.000, 0x000000],
  [0.004, 0x000000],
  [0.012, 0x0B0B0B],
  [0.024, 0x1A1A1A],
  [0.027, 0x1F1F1F],
  [0.043, 0x2D2D2D],
  [0.047, 0x353535],
  [0.090, 0x686868],
  [0.106, 0x797979],
  [0.145, 0xA7A7A7],
  [0.149, 0xA7A7A8],
  [0.188, 0x7C85A3],
  [0.204, 0x6977A0],
  [0.259, 0x2F4791],
  [0.282, 0x0F328B],
  [0.290, 0x042B88],
  [0.294, 0x052790],
  [0.302, 0x031EA0],
  [0.306, 0x0724A8],
  [0.318, 0x0A43C3],
  [0.322, 0x0149C0],
  [0.325, 0x035CC5],
  [0.333, 0x0979CD],
  [0.337, 0x008DCC],
  [0.341, 0x00A3CF],
  [0.349, 0x00A9C7],
  [0.353, 0x00B9CA],
  [0.357, 0x00C3C2],
  [0.361, 0x00D1BC],
  [0.365, 0x00D9B2],
  [0.384, 0x00E189],
  [0.388, 0x00E37E],
  [0.427, 0x00F603],
  [0.431, 0x08F801],
  [0.447, 0x35FE00],
  [0.451, 0x3BFC00],
  [0.467, 0x6EFB00],
  [0.471, 0x73FD00],
  [0.502, 0xBCFF00],
  [0.545, 0xF8FA00],
  [0.557, 0xFAF000],
  [0.600, 0xF6B300],
  [0.612, 0xFA9E00],
  [0.620, 0xFD8900],
  [0.627, 0xFC7B00],
  [0.776, 0xFF0D12],
  [0.780, 0xFE0F17],
  [0.784, 0xFD141B],
  [0.792, 0xFF2C34],
  [0.808, 0xFE6764],
  [0.812, 0xF76C6B],
  [0.827, 0xF9ACA8],
  [0.831, 0xF3B1AC],
  [0.839, 0xFBCAC4],
  [0.847, 0xF9DDDD],
  [0.878, 0xFFFBFF],
  [1.000, 0xFFFFFF]
];

export const zeissThicknessGradient = [
  [0.000, 0x000000],
  [0.275, 0xA5A5A5],
  [0.286, 0xA6A6A9],
  [0.443, 0x072797],
  [0.447, 0x032497],
  [0.451, 0x002096],
  [0.459, 0x002FAC],
  [0.467, 0x0039B4],
  [0.475, 0x004CBE],
  [0.494, 0x008BCC],
  [0.506, 0x00AFCB],
  [0.522, 0x00CDC1],
  [0.533, 0x00DDA7],
  [0.561, 0x00E75F],
  [0.596, 0x06F800],
  [0.600, 0x0CF900],
  [0.616, 0x30FF00],
  [0.698, 0xDEFE00],
  [0.733, 0xFFF800],
  [0.804, 0xFFA000],
  [0.835, 0xFF6800],
  [0.933, 0xFF0E0E],
  [0.937, 0xFF1C1C],
  [1.000, 0xFFFFFF]
];

export const zeissDifferenceGradient = [
  [0.000, 0x0000FF],
  [0.250, 0x00FF00],
  [0.500, 0xFFFFFF],
  [0.750, 0xFFFF00],
  [1.000, 0xFF0000]
];

export const heidelbergDifferenceGradient = [
  [0.000, 0x00FF00],
  [0.250, 0x007700],
  [0.500, 0x000000],
  [0.750, 0x770000],
  [1.000, 0xFF0000]
];

export const PALETTE_LENGTH = 256;

// see: https://github.com/Erkaman/glsl-gradient-palette
// see: https://gamedev.stackexchange.com/questions/43294/creating-a-retro-style-palette-swapping-effect-in-opengl
export function generatePalette(gradient) {
  var length = PALETTE_LENGTH;
  var palette = [];
  let g = 0; // current gradient index
  for (let p = 0; p < length; p++) {
    let fraction = p / length;
    if (gradient[g + 1][0] < fraction) g++;
    let c1 = gradient[g];
    let c2 = gradient[g + 1];
    let distance = (fraction - c1[0]) / (c2[0] - c1[0]);
    palette.push(...mixColors(c1[1], c2[1], distance));
  }
  var typed = new Uint8Array(palette);
  return typed;
}

export const octThicknessPalette = 
{
  isInverted: false,
  colorPalette: generatePalette(octThicknessGradient)
};

function mixColors(c1, c2, dist) {
  const r1 = getByte(c1, RED);
  const g1 = getByte(c1, GREEN);
  const b1 = getByte(c1, BLUE);
  const r2 = getByte(c2, RED);
  const g2 = getByte(c2, GREEN);
  const b2 = getByte(c2, BLUE);
  return [mixByte(r1, r2, dist), mixByte(g1, g2, dist), mixByte(b1, b2, dist)];
}

function mixByte(b1, b2, dist) {
  return Math.floor(b1 * (1 - dist) + b2 * dist);
}

export function applyOCTPixelShading(readBuffer, writeBuffer, settings) {
  for (let i = 0; i < readBuffer.length; i++) {
    let pixel = readBuffer[i];

    if (settings.isInverted) 
      pixel = invertColor(pixel);
    else if (settings.colorPalette)
    {
      pixel = paletteColor(pixel, settings.colorPalette, settings.linearAdjustment);
    }

    writeBuffer[i] = pixel;
  }
}

function invertColor(pixel){
  const r = 255 - getByte(pixel, RED);
  const g = 255 - getByte(pixel, GREEN);
  const b = 255 - getByte(pixel, BLUE);
  return createPixel(r, g, b, getByte(pixel, ALPHA));
}

function paletteColor(pixel, palette, adjustment){
  // the images are grayscale, so the color selection is arbitrary
  let oldR = getByte(pixel, RED);
  if (adjustment) oldR = Math.floor(adjustment.slope * (oldR + adjustment.offset));
  if (oldR > PALETTE_LENGTH - 1)
    oldR = PALETTE_LENGTH - 1;
  const r = palette[oldR * 3];
  const g = palette[oldR * 3 + 1];
  const b = palette[oldR * 3 + 2];
  // the palette is in BGR, so flip r and b
  return createPixel(b, g, r, getByte(pixel, ALPHA));
}

export function drawEllipse(ctx, centerX, centerY, radiusX, radiusY){
  if (ctx.ellipse) ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
  else drawEllipseBezier(ctx, centerX, centerY, radiusX, radiusY);
}

// https://stackoverflow.com/questions/14169234/the-relation-of-the-bezier-curve-and-ellipse
export function drawEllipseBezier(ctx, centerX, centerY, radiusX, radiusY){
  let kappa = 0.5522848, // magic number
  ox = radiusX * kappa, // horizontal control point
  oy = radiusY * kappa, // vertical control point
  xs = centerX - radiusX, // left
  ys = centerY - radiusY, // top
  xe = centerX + radiusX, // right
  ye = centerY + radiusY, // bottom
  xm = centerX, // center x
  ym = centerY; // center y

  ctx.moveTo(xs, ym);
  ctx.bezierCurveTo(xs, ym - oy, xm - ox, ys, xm, ys);
  ctx.bezierCurveTo(xm + ox, ys, xe, ym - oy, xe, ym);
  ctx.bezierCurveTo(xe, ym + oy, xm + ox, ye, xm, ye);
  ctx.bezierCurveTo(xm - ox, ye, xs, ym + oy, xs, ym);
}

export function calculatePixelDifference(img, offsetX, offsetY, compareBuffer, baselineBuffer, writeBuffer, settings){
    let palette = settings.colorPalette;
    let adjust = settings.linearAdjustment;
    for (let y = 0; y < img.height; y++) {
    for (let x = 0; x < img.width; x++) {
      let baselineX = x + offsetX;
      if (baselineX < 0 || baselineX >= img.width) continue;
      let baselineY = y + offsetY;
      if (baselineY < 0 || baselineY >= img.height) continue;

      let compareIndex = y * img.width + x;
      let comparePixel = compareBuffer[compareIndex];
      let baselineIndex = baselineY * img.width + baselineX;
      let baselinePixel = baselineBuffer[baselineIndex];
      if (comparePixel === undefined || baselinePixel === undefined) continue;
      let compareRed = getByte(comparePixel, RED);
      let baselineRed = getByte(baselinePixel, RED);

      let gray = compareRed - baselineRed;
      let gray_adjust = Math.floor(adjust.slope * (gray + adjust.offset));
      if (gray_adjust >= PALETTE_LENGTH)
        gray_adjust = PALETTE_LENGTH - 2;
      else if (gray_adjust <= 0)
        gray_adjust = 0;
      let b = palette[gray_adjust * 3] || 0;
      let g = palette[gray_adjust * 3 + 1] || 0;
      let r = palette[gray_adjust * 3 + 2] || 0;
      let pixel = createPixel(r, g, b, (r || g || b) ? 255 : 0);

      writeBuffer[compareIndex] = pixel;
    }
  }
}

export function generateFlatImage(context) {
  if (context.clearActiveAnnotation) context.clearActiveAnnotation();

  let img = context.image && (context.image.imgHtmlObject || context.image.thumbHtmlObject);
  let settings = context.imageColorSettings;

  let canvas = context.saveCanvas;
  canvas.width = img.width;
  canvas.height = img.height;
  let renderer = new CanvasRenderer(canvas);
  let imgSize = (context.image && context.image.imgSize);
  if (!imgSize || !imgSize.w || !imgSize.h) imgSize = context.image.thumbSize;
  renderer.doScaleToPixelRatio = false;
  renderer.canvasSize = imgSize;
  renderer.imgSize = imgSize;
  renderer.isFlipped = context.isFlipped;
  renderer.isFlipped90 = context.isFlipped90;
  if (hasCustomPixelColorSettings(settings)) {
    // note: this method puts image data directly onto the canvas
    drawShadedImage(img, canvas, applyPixelShadingRules, settings);
  } else {
    renderer.drawImg(img);
  }
  // note: using "render" instead of "draw" here because draw erases the canvas!
  let ctx = canvas.getContext("2d");
  context.annotations.forEach(a => renderer.renderAnnotation(a, ctx));

  if (context.setFlattenedImageReady) context.setFlattenedImageReady(true);
} 